import { useState, useEffect, useMemo } from 'react';
import { t } from 'i18n';
import {
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Flex,
  Title,
  Body,
  LazyImage,
  Button,
  Shape,
  styled,
  SubTitle,
  Caption,
  Glide,
  Tooltip,
  CustomLink,
} from '@teko/ds-desktop';
import { GiftColored, ShopColored, UnionOutlined } from '@teko/ds-icon';
import { ThemeProps } from '@teko/ui';
import {
  IActiveCombo,
  IComboWithKey,
  ISelectingProduct,
  IConditionProduct,
  IComboGroup,
} from 'types';
import {
  countAndGetBenefitDescription,
  getTotalDiscount,
  getTotalAmount,
  getDiscountByCombo,
} from 'helpers';
import { useCartV2, useTenantConfig } from 'contexts';
import { PromotionBenefitInfo } from './PromotionBenefitInfo';
import { ComboProductPrice } from './ComboProductPrice';
import { getConditionProductLinkObj } from 'modules/cart/utils';
import { getTrackingData } from '@teko/tracking';
import { formatCurrency } from '@teko/teko-ecommerce';

export interface IProductSelectionModalProps {
  selectingProduct?: Pick<ISelectingProduct, 'conditionKey'> & {
    sku: string;
  };
  visible: boolean;
  comboGroup?: IComboGroup;
  setComboGroup: (comboGroup?: IComboGroup) => void;
  closeModal: (selectingCombo?: IActiveCombo) => void;
  activeCombo?: IActiveCombo;
  setActiveCombo: (combo?: IActiveCombo) => void;
  productCombos?: IComboWithKey[];
  handleConfirm?: (selectingCombo?: IActiveCombo) => void;
  showAddComboToCartButton?: boolean;
  showAllCombos?: boolean;
}

export const ProductSelectionModal: React.FC<IProductSelectionModalProps> = ({
  selectingProduct,
  visible,
  closeModal,
  activeCombo,
  setActiveCombo,
  productCombos,
  comboGroup,
  setComboGroup,
  handleConfirm,
  /** show/hide button "add combo to cart" in modal's footer */
  showAddComboToCartButton = true,
  /**
   * wether to show all combos available to current active combo or only combos that has the same totalProduct
   * showAllCombos = false --> filter & show only combo that has the same totalProduct
   * showAllCombos = true --> show all combos available to current active combo
   */
  showAllCombos = false,
}) => {
  const { onAddComboItems } = useCartV2();
  // Index of condition in its combo.
  // If user do not select any condition, default is the first condition in combo immediately after main product (main product's index is 0)
  const [conditionIndex, setConditionIndex] = useState(
    selectingProduct ? selectingProduct.conditionKey - 1 : 1,
  );
  const [selectingComboProduct, setSelectingComboProduct] = useState<{
    sku: string;
    conditionKey: number;
  }>();
  const [selectingCombo, setSelectingCombo] = useState(activeCombo);
  const { numberDisplayFormat, applyNewLogicToCalculateComboPrice = false } =
    useTenantConfig();

  const updateActiveAndGroupCombo = () => {
    if (comboGroup && selectingCombo && selectingComboProduct) {
      const selectedGroup = comboGroup[selectingCombo.totalProduct];
      setActiveCombo(selectingCombo);
      setComboGroup({
        ...comboGroup,
        [selectingCombo.totalProduct]: {
          combos: selectedGroup.combos.map((cb) =>
            cb.key === selectingCombo.key ? selectingCombo : cb,
          ),
        },
      });
    }
  };

  const selectComboProduct = (
    product: IConditionProduct,
    cb: IComboWithKey,
  ) => {
    if (selectingCombo) {
      // build new active combo based on the new selected product
      const comboTemp = {
        ...cb,
        // Check if the select combo product in still in side the previous combo
        conditions:
          selectingCombo.key === cb.key
            ? //If yes, only update new product to current active index & not change other indexes
              selectingCombo.conditions.map((condition, index) => {
                return {
                  key: condition.key,
                  requiredQuantity: condition.requiredQuantity,
                  product:
                    index === conditionIndex ? product : condition.product,
                };
              })
            : // If not, update new product to current active index &
              // reset other indexes in active combo to products[0]
              cb.conditions.map((condition, index) => {
                return {
                  key: condition.key,
                  requiredQuantity: condition.requiredQuantity,
                  product:
                    index === conditionIndex ? product : condition.products[0],
                };
              }),
      };

      const newActiveCombo = {
        ...comboTemp,
        discountByCombo: getDiscountByCombo(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
        totalDiscount: getTotalDiscount(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
        totalAmount: getTotalAmount(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
      };

      setSelectingCombo(newActiveCombo);
      setSelectingComboProduct({
        sku: product.sku,
        conditionKey: cb.conditions[conditionIndex].key,
      });
    }
  };

  const addComboToCart = async () => {
    if (selectingCombo) {
      updateActiveAndGroupCombo();
      const mainPayload = {
        override: true,
        groups: [
          {
            quantity: 1,
            promotionId: selectingCombo.promotion.id,
            products: selectingCombo.conditions.map((el, index) => ({
              sku: el.product.sku,
              promotionId: !!el.product.bestPromotionIds?.length
                ? el.product.bestPromotionIds?.[0]
                : null,
              quantity: el.requiredQuantity,
              isPrimary: index === 0,
            })),
          },
        ],
      };

      await onAddComboItems(mainPayload, false);
      closeModal(selectingCombo);
    }
  };

  // List of combos have same product quantity with active combo
  const similarCombos = useMemo(() => {
    if (selectingCombo && productCombos?.length) {
      return showAllCombos
        ? productCombos
        : productCombos.filter(
            (item) => item.totalProduct === selectingCombo.totalProduct,
          );
    }
    return [];
  }, [selectingCombo, productCombos]);

  // Trigger open this modal, if have no active combo -> set default active combo is the first combo in productCombos
  useEffect(() => {
    if (visible && !activeCombo && productCombos?.length) {
      const comboTemp = {
        ...productCombos[0],
        conditions: productCombos[0].conditions.map(
          ({ products, ...restCondition }) => ({
            ...restCondition,
            product: products[0],
          }),
        ),
      };
      const defaultCombo = {
        ...comboTemp,
        discountByCombo: getDiscountByCombo(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
        totalDiscount: getTotalDiscount(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
        totalAmount: getTotalAmount(
          comboTemp,
          applyNewLogicToCalculateComboPrice,
        ),
      };
      setActiveCombo(defaultCombo);
      setSelectingCombo(defaultCombo);
    }
  }, [activeCombo, visible]);

  useEffect(() => {
    if (!selectingComboProduct) {
      if (selectingProduct) {
        setSelectingComboProduct(selectingProduct);
      } else {
        if (activeCombo) {
          setSelectingComboProduct({
            sku: activeCombo.conditions[1]?.product.sku,
            conditionKey: activeCombo.conditions[1]?.key,
          });
        }
      }
    }
  }, [activeCombo, selectingProduct]);

  const ComboProductImage = ({
    condition,
    index,
    selectingCombo,
  }: {
    condition: {
      key: number;
      product: IConditionProduct;
    };
    index: number;
    selectingCombo: IActiveCombo;
  }) => {
    // Only show tooltip when length of conditions in combo > 2
    // If selecting product is the last item -> show tooltip for previous item, else show tooltip for next item
    const [showTooltip, setShowTooltip] = useState(
      selectingCombo.conditions.length > 2 &&
        (conditionIndex === selectingCombo.conditions.length - 1
          ? index === conditionIndex - 1
          : index === conditionIndex + 1),
    );
    return (
      <Tooltip
        key={index}
        title={
          <div
            onClick={() => setShowTooltip(false)}
            style={{ position: 'relative' }}
          >
            <UnionOutlined
              size={18}
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                backgroundColor: '#434657',
                borderRadius: '100px',
              }}
              color="#fff"
            />
            {t('Chọn sản phẩm còn lại trong combo')}
          </div>
        }
        show={showTooltip}
        triggerHover={false}
      >
        <ImageWrapper
          highlight={index === conditionIndex}
          {...getTrackingData({
            isTrackContent: true,
            contentName: 'changeProductInCombo',
            regionName: 'detailProductComboList',
            payload: JSON.stringify({
              skuComboIndex: index + 1,
            }),
          })}
        >
          <LazyImage
            key={index}
            src={condition.product.imageUrl}
            borderRadius={4}
            border
            contain
            onClick={
              index !== 0
                ? () => {
                    setConditionIndex(index);
                    setSelectingComboProduct({
                      sku: condition.product.sku,
                      conditionKey: condition.key,
                    });
                    setShowTooltip(false);
                  }
                : undefined
            }
          />
          {index === 0 && <StyledNotSelectImageBg />}
        </ImageWrapper>
      </Tooltip>
    );
  };

  return selectingCombo ? (
    <Modal
      visible={visible}
      width="75%"
      onClose={closeModal}
      footer={false}
      {...getTrackingData({
        isTrackContent: true,
        contentName: 'popupChangeProductCombo',
        regionName: 'detailProductComboList',
      })}
    >
      <ModalHeader padding="s0" marginBottom="s24">
        <Flex direction="column" gap="s4">
          <Title level={1}>{t('Chọn sản phẩm trong combo')}</Title>
          <Body level={1}>
            {t('Các combo khác nhau có thể có ưu đãi khác nhau')}
          </Body>
        </Flex>
      </ModalHeader>
      <ModalBody maxHeight={500} style={{ overflowY: 'scroll' }} padding="s0">
        {similarCombos.map((cb, index) => {
          // not render combo if condition at conditionIndex not have any products
          if (!cb.conditions[conditionIndex]?.products.length) {
            return;
          }
          return (
            <StyledWrapperComboItem
              key={index}
              {...getTrackingData({
                isTrackContent: true,
                contentName: `blockComboPromotion${index + 1}`,
                regionName: 'detailProductComboList',
                payload: JSON.stringify({ comboIndex: index + 1 }),
              })}
            >
              <Flex marginBottom="s16" align="center">
                <ShopColored size={20} />
                <SubTitle level={3} marginLeft="s4">
                  Combo {index + 1}
                </SubTitle>
              </Flex>
              {cb.promotion.benefit &&
                countAndGetBenefitDescription(
                  cb.promotion.benefit,
                  numberDisplayFormat,
                ).numberOfBenefit >= 1 && (
                  <div style={{ marginBottom: '16px' }}>
                    <Caption level={2} color="textSecondary">
                      {t('Bạn nhận được').toUpperCase()}
                    </Caption>
                    <PromotionBenefitInfo
                      promotionBenefit={cb.promotion.benefit}
                      backgroundColor="white"
                    />
                  </div>
                )}
              <Glide perView={(window.innerWidth * 0.7) / 220}>
                {cb.conditions[conditionIndex]?.products.map((product, i) => {
                  const productLink = getConditionProductLinkObj(product);
                  const isSelectedProduct =
                    cb.key === selectingCombo?.key &&
                    cb.conditions[conditionIndex].key ===
                      selectingComboProduct?.conditionKey &&
                    product.sku === selectingComboProduct?.sku;
                  return (
                    <ProductItemWrapper key={i}>
                      <CustomLink {...productLink}>
                        <LazyImage
                          src={product.imageUrl}
                          hover="zoom"
                          contain
                          alt={product.name}
                          aspectRatio={1}
                          width={166}
                          height={166}
                        />
                      </CustomLink>
                      {cb.conditions[conditionIndex].requiredQuantity > 1 && (
                        <QuantityTag backgroundColor="primary500">
                          {cb.conditions[conditionIndex].requiredQuantity}x
                        </QuantityTag>
                      )}
                      <CustomLink {...productLink}>
                        <Body
                          hoverColor="primary500"
                          marginTop="s8"
                          level={1}
                          line={3}
                          color="#434657"
                          height={60}
                        >
                          {product.name}
                        </Body>
                      </CustomLink>
                      <div style={{ width: '100%', alignItems: 'center' }}>
                        <ComboProductPrice
                          price={product.price}
                          promotion={cb.promotion}
                        />
                      </div>
                      <div
                        style={{ width: '100%', alignItems: 'center' }}
                        {...getTrackingData({
                          isTrackContent: true,
                          contentName: 'selectProduct',
                          regionName: 'detailProductComboList',
                          payload: JSON.stringify({
                            comboIndex: index + 1,
                            skuIndex: i + 1,
                            sku: product.sku,
                          }),
                        })}
                      >
                        <Button
                          variant="secondary"
                          width="100%"
                          marginTop="s8"
                          onClick={() => selectComboProduct(product, cb)}
                          disabled={isSelectedProduct}
                        >
                          <SubTitle level={4} weight="medium">
                            {isSelectedProduct ? t('Đã chọn') : t('Chọn')}
                          </SubTitle>
                        </Button>
                      </div>
                    </ProductItemWrapper>
                  );
                })}
              </Glide>
            </StyledWrapperComboItem>
          );
        })}
      </ModalBody>
      <ModalFooter
        borderTop="1px solid #E4E5F0"
        padding="s0"
        style={{ display: 'grid', gridTemplateColumns: '100%' }}
      >
        {selectingCombo?.promotion.benefit && (
          <Flex
            gap="s4"
            justify="start"
            align="center"
            marginTop="s8"
            marginBottom="s8"
          >
            <GiftColored size={20} />
            <Body level={1} line={2}>
              {`${t('Bạn nhận được')}: `}
              <span style={{ fontWeight: 'bold' }}>
                {
                  countAndGetBenefitDescription(
                    selectingCombo.promotion.benefit,
                    numberDisplayFormat,
                  ).benefitDescription
                }
              </span>
            </Body>
          </Flex>
        )}
        <Flex justify="space-between" align="center">
          <Flex gap="s12">
            {selectingCombo.conditions.map((c, index) => (
              <ComboProductImage
                key={index}
                condition={c}
                index={index}
                selectingCombo={selectingCombo}
              />
            ))}
          </Flex>
          <Flex align="center">
            <Shape marginRight="s32">
              <Flex>
                <Body level={1} marginRight="s8">{`${t('Tiết kiệm')}:`}</Body>
                <Body level={1}>
                  {formatCurrency(
                    selectingCombo.totalDiscount,
                    numberDisplayFormat,
                  )}
                </Body>
              </Flex>
              <>
                {/* This section display total amount */}
                <Flex>
                  <Body level={2} marginRight="s4">{`${t('Tổng tiền')}:`}</Body>
                  <Title level={2} color="primary500">
                    {formatCurrency(
                      selectingCombo.totalAmount,
                      numberDisplayFormat,
                    )}
                  </Title>
                </Flex>
              </>
            </Shape>
            {showAddComboToCartButton && (
              <div
                {...getTrackingData({
                  isTrackContent: true,
                  contentName: 'btnAddToCart',
                  regionName: 'detailProductComboList',
                  payload: JSON.stringify({
                    promotionType: 'combo',
                    comboTab: `Combo ${selectingCombo.totalProduct} sản phẩm`,
                    comboPromotion: selectingCombo.promotion.id,
                    comboIndex: selectingCombo.key,
                    skus: selectingCombo.conditions.map((c) => c.product.sku),
                  }),
                })}
              >
                <Button
                  variant="secondary"
                  marginRight="s16"
                  onClick={addComboToCart}
                >
                  {t('Thêm vào giỏ')}
                </Button>
              </div>
            )}
            <div
              {...getTrackingData({
                isTrackContent: true,
                contentName: 'btnConfirmCombo',
                regionName: 'detailProductComboList',
                payload: JSON.stringify({
                  comboPromotion: selectingCombo.promotion.id,
                }),
              })}
            >
              <Button
                variant="primary"
                onClick={() => {
                  updateActiveAndGroupCombo();
                  handleConfirm?.(selectingCombo);
                  closeModal(selectingCombo);
                  setSelectingComboProduct(undefined);
                  setSelectingCombo(undefined);
                }}
              >
                {t('Xác nhận')}
              </Button>
            </div>
          </Flex>
        </Flex>
      </ModalFooter>
    </Modal>
  ) : null;
};

const StyledWrapperComboItem = styled.div`
  width: 100%;
  border-radius: 8px;
  background-color: #f8f8fc;
  padding: 16px;
  margin-bottom: 12px;
`;

const ProductItemWrapper = styled.div`
  width: 208px;
  background-color: #ffffff;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid #e4e5f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  position: relative;
`;

const QuantityTag = styled(Shape)`
  position: absolute;
  height: 16px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: white;
  padding: 2px 5px;
  top: 8px;
  left: 8px;
`;

const ImageWrapper = styled.div<ThemeProps & { highlight: boolean }>(
  ({ theme, highlight }: ThemeProps & { highlight: boolean }) => `
    border: ${highlight ? `1.5px solid ${theme?.brand.primary}` : 'none'};
    border-radius: 4px;
    height: 72px;
    width: 72px;
    position: relative;
`,
);

const StyledNotSelectImageBg = styled.div`
  position: absolute;
  height: 72px;
  width: 72px;
  background-color: rgba(27, 29, 41, 0.5);
  z-index: 1;
  top: 0;
  left: 0;
  border-radius: 4px;
`;
